import React, { useRef } from "react";
import FroalaEditor from "react-froala-wysiwyg";
import "froala-editor/js/froala_editor.pkgd.min.js";

// Import required Froala plugins
import "froala-editor/js/plugins/align.min.js";
import "froala-editor/js/plugins/char_counter.min.js";
import "froala-editor/js/plugins/code_view.min.js"; // Code view plugin for HTML source editing
import "froala-editor/js/plugins/colors.min.js";
import "froala-editor/js/plugins/font_family.min.js";
import "froala-editor/js/plugins/font_size.min.js";
import "froala-editor/js/plugins/image.min.js";
import "froala-editor/js/plugins/link.min.js";
import "froala-editor/js/plugins/lists.min.js";
import "froala-editor/js/plugins/paragraph_format.min.js";
import "froala-editor/js/plugins/table.min.js";
import "froala-editor/js/plugins/special_characters.min.js";
import "froala-editor/js/plugins/file.min.js";
interface FroalaEditorComponentProps {
  value: string;
  onChange: (content: string) => void;
  placeholder?: string;
  height?: any;
  readonly?: boolean;
  isPublished?: boolean;
  apiUrl?: string;
}

const FroalaEditorComponent: React.FC<FroalaEditorComponentProps> = ({
  value,
  onChange,
  placeholder = "Enter text here...",
  height = 300,
  readonly = false,
  isPublished = false,
  apiUrl = process.env.REACT_APP_PIPELINE_API,
}) => {

  const editorRef = useRef<any>(null);

  const handleModelChange = (content: string) => {
    if (!isPublished) {
      onChange(content);
    }
  };

  const uploadUrl = `${apiUrl}autoGrade/editorUpload`;

  // Read at upload time so a refreshed Keycloak token is always used
  const getAuthHeaders = () => {
    const token = localStorage.getItem("autoGraderToken");
    const user = localStorage.getItem("user");
    return token ? { Authorization: `Bearer ${token}`, Email: user } : {};
  };


  return (
    <FroalaEditor
      tag="textarea"
      model={value}
      onModelChange={handleModelChange}
      config={{
        key: "te1C2sE6B5B4C5E3H3jC1QUd1Xd1OZJ1ABVJRDRNGGUE1ITrE1D4A3A10B1B6F6B1G4E3==",
        placeholderText: placeholder,
        fileUpload: true,
        fileUploadURL: uploadUrl, // Replace with your API endpoint
        fileUploadMethod: "POST",
        fileMaxSize: 20 * 1024 * 1024,
        fileAllowedTypes: [
          "application/pdf", // ✅ PDF

          // ✅ Word Documents
          "application/msword", // DOC (Older Word format)
          "application/vnd.openxmlformats-officedocument.wordprocessingml.document", // DOCX (Newer Word format)

          // ✅ Excel Spreadsheets
          "application/vnd.ms-excel", // XLS (Older Excel format)
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", // XLSX (Newer Excel format)

          // ✅ PowerPoint Presentations
          "application/vnd.ms-powerpoint", // PPT (Older PowerPoint format)
          "application/vnd.openxmlformats-officedocument.presentationml.presentation", // PPTX (Newer PowerPoint format)

          // ✅ CSV Files
          "text/csv",

          // ✅ Images
          "image/png", // PNG
          "image/jpeg", // JPEG
          "image/jpg", // JPG
          "image/gif", // GIF
          "image/webp", // WEBP
          "image/svg+xml", // SVG

          // ✅ Video
          "video/mp4", // MP4
        ],
        imageUploadURL: uploadUrl,
        imageUploadMethod: "POST",
        requestHeaders: getAuthHeaders(),
        height: height,
        toolbarButtons: [
          "html", // This button enables the Code View
          "bold",
          "italic",
          "underline",
          "|",
          "formatOL",
          "formatUL",
          "outdent",
          "indent",
          "|",
          "align",
          "insertLink",
          "insertTable",
          // "insertImage",
          "insertFile",
          "specialCharacters",
        ],
        pluginsEnabled: [
          "align",
          "table",
          "link",
          "image",
          "lists",
          "code_view",
          "specialCharacters",
          "file",
        ],
        htmlAllowedTags: [".*"], // Allows any HTML tag to be edited
        htmlAllowedAttrs: [".*"], // Allows any HTML attributes
        readonly: isPublished || readonly,
        events: {
          "initialized": function () {
            editorRef.current = this; // ✅ Store Froala editor instance
          },
          "file.beforeUpload": function (this: any) {
            this.opts.requestHeaders = getAuthHeaders();
          },
          "image.beforeUpload": function (this: any) {
            this.opts.requestHeaders = getAuthHeaders();
          },
          "image.uploaded": function (response: any) {
            const data = JSON.parse(response); // ✅ Parse API response
            if (data.url && editorRef.current) {
              editorRef.current.image.insert(data.url, true, null, editorRef.current.image.get(), null); // ✅ Insert image
            }
          },
          "image.uploadedError": function (error: any) {
            console.error("Image upload failed:", error);
          },

          // ✅ File Upload Configuration
          "file.uploaded": function (response: any) {
            try {
              const data = JSON.parse(response);
              if (data.url && editorRef.current) {
                const link = data.url;
                const fileName = data.url ? data.url.split("/").pop() : "";
                editorRef.current.file.insert(link, fileName, link, link, link);; // ✅ Insert file link
                return JSON.stringify({ link: link });
              }
              return
            } catch (error) {
              console.error("File upload response error:", error);
            }
          },
          "file.uploadedError": function (error: any) {
            console.error("File upload failed:", error);
          },
        },
      }}
    />
  );
};

export default FroalaEditorComponent;
